import React, { useEffect, useRef, useState } from "react";
import useVisibility from "../../../utils/useVisibility";
import Icon from "../../Icon/Icon";
import { Category, Highlight, InfiosContentTeaserGrid, ReadingTime, TeaserHighlight, TeaserNormal } from "./ContentTeaserGrid.style";
import sanitizeHtml from "sanitize-html";
import sanitizeWhiteList from "../../sanitize-html.whitelist";
import { urlHasParam } from "../../../utils/js/params";
import { getTagInfo } from "../../../utils/js/tags";
import { devices } from "../../../utils/styled/_variables.styled";
import { getWidthAndHeightProps } from "../../../utils/js/widthAndHeight";

export default function ContentTeaserGrid(props) {
    const {
        highlight,
        sectionParent,
        imageCrop,
        readingTime,
        date,
        title,
        linkText,
        linkPath,
        contentType,
        contentAction,
        description,
        background,
        onContentTypeClick,
        target,
        term,
        tagsInfo,
        isLinkless,
        showHighlightedDescription,
        shadow,
        blueTiles
    } = props;

    const [teaserHighlightRef, isTeaserHighlightVisible] = useVisibility();
    const [teaserNormalRef, isTeaserNormalVisible] = useVisibility();


    function handleCategoryClick(event, contentType) {
        if (!onContentTypeClick && !contentAction) {
            return;
        }
        if (contentAction) {
            if (contentAction[0].overviewPagePath === 'javascript:void(0)') return;
            event.preventDefault();
            window.open(contentAction[0].overviewPagePath, contentAction.overviewPageTarget);
            return;
        }
        event.preventDefault();
        onContentTypeClick(contentType);
    }

    const highlightTerm = (text) => {
        if (text && term) {
            const regex = new RegExp(term, "gi");
            return text.replace(regex, (match) => `<b style="font-weight: bold">${match}</b>`);
        }
        return text;
    };

    const [tagInfo, setTagInfo] = useState();

    useEffect(() => {
        setTagInfo(getTagInfo(contentType, tagsInfo))
    }, [tagsInfo])


    return (
        <InfiosContentTeaserGrid
            href={linkPath && `${linkPath}${
                urlHasParam("wcmmode=disabled") ? "?wcmmode=disabled" : ""
            }`}
            onClick={e => !linkPath && e.preventDefault()}
            target={target ? target : "_self"}
            className={`${sectionParent} ${highlight ? '' : 'normal'}`}
            isLinkless={isLinkless}
            linkPath={linkPath}
        >
            {highlight ? (
                <TeaserHighlight ref={teaserHighlightRef} blueTiles={blueTiles} shadow={shadow}
                    isLinkless={isLinkless} linkPath={linkPath} sectionParent={sectionParent}>
                    {!blueTiles && imageCrop && Object.keys(imageCrop).length && isTeaserHighlightVisible?
                    <picture>
                        <source media={devices.desktop} srcSet={imageCrop.newsHighlightedDesktop} />
                        <source media={devices.tablets} srcSet={imageCrop.newsHighlightedTablet} />
                        <source media={devices.mobile} srcSet={imageCrop.newsHighlightedTablet} />
                        <img {...getWidthAndHeightProps(teaserHighlightRef)} className="teaser__bg" src={imageCrop.newsHighlightedDesktop} alt={title} />
                    </picture>
                    :
                    !blueTiles && <img {...getWidthAndHeightProps(teaserHighlightRef)} src="https://placehold.co/644x644?text=Missing+Image" className="teaser__bg" alt={title} />
                    }
                    <div className="hover__overlay" />
                    <div className="teaser__inner">
                        <div className="teaser__highlight__header">
                            {(contentType || readingTime) && <div className="teaser__tag">
                                {contentType && (
                                    <a href={tagInfo ? tagInfo.overviewPagePath : 'javascript:void()'} target={tagInfo ? tagInfo.overviewPageTarget : ''}>
                                        <Category>{contentType}</Category>
                                    </a>
                                )}
                                {sectionParent !== "bluetiles" && (
                                    <Highlight>Highlight</Highlight>
                                )}
                            </div>}
                            {readingTime && (
                                <ReadingTime>
                                    <Icon color="white" icon="eye-filled" />
                                    <span>{readingTime.includes('min') ? readingTime : `${readingTime} min`}</span>
                                </ReadingTime>
                            )}
                        </div>
                        <div className="teaser__highlight__bottom">
                            {date && <span className="teaser__date">{date}</span>}
                            <h5 className="teaser__title">{title}</h5>
                            {sectionParent === "bluetiles" && description && (
                                <div className="description" dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(description, sanitizeWhiteList),
                                }}/>
                            )}
                            {showHighlightedDescription && description && (
                                <div className="description teaser__description">
                                    <p className="description">{description}</p>
                                </div>
                            )}
                        </div>
                        {linkPath && (
                            <div className="teaser__cta">
                                {linkText}
                                <Icon color="white" icon="cta-arrow"/>
                            </div>
                        )}
                    </div>
                </TeaserHighlight>
            ) : (
                <TeaserNormal ref={teaserNormalRef} shadow={shadow}
                    backgroundColor={background}>
                    <div className="hover__shadow"></div>
                    <div className="top" >
                        {imageCrop && Object.keys(imageCrop).length && isTeaserNormalVisible?
                        <picture>
                            <source media={devices.desktop} srcSet={imageCrop.newsDesktop} />
                            <source media={devices.tablets} srcSet={imageCrop.newsTablet} />
                            <source media={devices.mobile} srcSet={imageCrop.newsMobile} />
                            <img {...getWidthAndHeightProps(teaserNormalRef)} src={imageCrop.newsDesktop} alt={title} className="top__bg" />
                        </picture>
                        :
                        <img {...getWidthAndHeightProps(teaserNormalRef)} className="top__bg" src="https://placehold.co/644x644?text=Missing+Image" alt={title} />
                        }
                        <div className="teaser__normal__header">
                            {contentType && (
                                // eslint-disable-next-line no-script-url
                                <a href={tagInfo ? tagInfo.overviewPagePath : 'javascript:void()'}
                                   target={tagInfo ? tagInfo.overviewPageTarget : ''}>
                                    <Category>{contentType}</Category>
                                </a>
                            )}
                            {readingTime && (
                                <ReadingTime>
                                    <Icon color="white" icon="eye-filled"/>
                                    <span>{readingTime.includes('min') ? readingTime : `${readingTime} min`}</span>
                                </ReadingTime>
                            )}
                        </div>
                    </div>
                    <div className="teaser__normal__content">
                        {date && <span className="teaser__date">{date}</span>}
                        <h5 className="teaser__title">{title}</h5>
                        <p className="teaser__text"
                           dangerouslySetInnerHTML={{__html: sanitizeHtml(highlightTerm(description), sanitizeWhiteList)}}></p>
                        <div className="card__spacer"></div>
                        <div className="teaser__cta">
                            {linkText}
                            <Icon color="#0060FF" icon="cta-arrow"/>
                        </div>
                    </div>
                </TeaserNormal>
            )}
        </InfiosContentTeaserGrid>
    );
}
